import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { ClipboardText, Plus, Trash, DotsSixVertical, PencilSimple, TextT, Hash, CheckSquare, CaretDown, Calendar, Camera, PenNib, TextAlignLeft, Eye, TextHOne } from "@phosphor-icons/react";
import { toast } from "sonner";

import DateInput from "../components/DateInput";
const FIELD_TYPES = [
  { value: "text", label: "Short Text", icon: TextT },
  { value: "textarea", label: "Long Text", icon: TextAlignLeft },
  { value: "number", label: "Number", icon: Hash },
  { value: "checkbox", label: "Checkbox", icon: CheckSquare },
  { value: "dropdown", label: "Dropdown", icon: CaretDown },
  { value: "date", label: "Date", icon: Calendar },
  { value: "photo", label: "Photo Upload", icon: Camera },
  { value: "signature", label: "Signature", icon: PenNib },
  { value: "section", label: "Section heading", icon: TextHOne },
];

export default function JobFormsPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create/Edit form state
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);

  // Preview
  const [showPreview, setShowPreview] = useState(false);

  const fetchForms = useCallback(async () => {
    try {
      const { data } = await API.get("/job-forms");
      setForms(data.forms || []);
    } catch { toast.error("Failed to load forms"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const resetEditor = () => {
    setEditingId(null); setTitle(""); setDescription(""); setFields([]);
  };

  const openCreate = () => { resetEditor(); setShowEditor(true); };

  const openEdit = (form) => {
    setEditingId(form.id);
    setTitle(form.title);
    setDescription(form.description || "");
    setFields(form.fields || []);
    setShowEditor(true);
  };

  const addField = (type) => {
    setFields(prev => [...prev, {
      id: `f-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type,
      label: "",
      required: false,
      placeholder: "",
      options: type === "dropdown" ? [""] : [],
    }]);
  };

  const updateField = (index, key, value) => {
    setFields(prev => prev.map((f, i) => i === index ? { ...f, [key]: value } : f));
  };

  const removeField = (index) => {
    setFields(prev => prev.filter((_, i) => i !== index));
  };

  const moveField = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= fields.length) return;
    const copy = [...fields];
    const [moved] = copy.splice(fromIdx, 1);
    copy.splice(toIdx, 0, moved);
    setFields(copy);
  };

  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);

  const handleFieldDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.style.opacity = "0.4";
  };
  const handleFieldDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    if (dragIdx !== null && dropIdx !== null && dragIdx !== dropIdx) moveField(dragIdx, dropIdx);
    setDragIdx(null);
    setDropIdx(null);
  };
  const handleFieldDragOver = (e, idx) => {
    e.preventDefault();
    setDropIdx(idx);
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (fields.length === 0) { toast.error("Add at least one field"); return; }
    for (let i = 0; i < fields.length; i++) {
      if (!fields[i].label.trim()) { toast.error(`Field ${i + 1} needs a label`); return; }
    }

    setSaving(true);
    try {
      if (editingId) {
        await API.put(`/job-forms/${editingId}`, { title, description, fields });
        toast.success("Form updated");
      } else {
        await API.post("/job-forms", { title, description, fields });
        toast.success("Form created");
      }
      setShowEditor(false);
      resetEditor();
      fetchForms();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm("Delete this form template?")) return;
    try {
      await API.delete(`/job-forms/${formId}`);
      toast.success("Form deleted");
      fetchForms();
    } catch { toast.error("Failed to delete"); }
  };

  const getIcon = (type) => {
    const ft = FIELD_TYPES.find(t => t.value === type);
    return ft ? ft.icon : TextT;
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="job-forms-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Job Forms</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Create form templates for suppliers to complete on-site</p>
        </div>
        <Button onClick={openCreate} data-testid="create-form-btn"><Plus size={16} className="mr-1" /> New Form</Button>
      </div>

      {/* Forms list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : forms.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <ClipboardText size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
          <p className="text-sm text-muted-foreground mb-3">No forms created yet</p>
          <Button onClick={openCreate}>Create Your First Form</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map(f => (
            <div key={f.id} className="border border-border rounded-md p-4 bg-card hover:border-primary/40 transition-colors group" data-testid={`form-card-${f.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-semibold">{f.title}</h3>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(f)} data-testid={`edit-form-${f.id}`}><PencilSimple size={12} /></Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(f.id)} data-testid={`delete-form-${f.id}`}><Trash size={12} /></Button>
                </div>
              </div>
              {f.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{f.description}</p>}
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <span>{(f.fields || []).filter(x => x.type !== "section").length} fields</span>
                <span>Created {new Date(f.created_at).toLocaleDateString("en-GB")}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(f.fields || []).slice(0, 4).map((field, i) => {
                  const Icon = getIcon(field.type);
                  return <span key={i} className="flex items-center gap-0.5 text-[9px] bg-muted px-1.5 py-0.5 rounded"><Icon size={10} /> {field.label}</span>;
                })}
                {(f.fields?.length || 0) > 4 && <span className="text-[9px] bg-muted px-1.5 py-0.5 rounded">+{f.fields.length - 4} more</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={(open) => { if (!open) { setShowEditor(false); resetEditor(); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="form-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editingId ? "Edit Form" : "Create Form"}</DialogTitle>
            <DialogDescription>Build a form template for suppliers to fill in during jobs.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Form Title *</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Health & Safety Checklist" className="mt-1" data-testid="form-title-input" /></div>
              <div className="sm:col-span-2"><Label>Description</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Brief description of this form's purpose..." className="mt-1" /></div>
            </div>

            {/* Fields */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground">FORM FIELDS ({fields.length})</p>
                <Button variant="outline" size="sm" className="text-xs h-6" onClick={() => setShowPreview(true)} disabled={fields.length === 0}><Eye size={12} className="mr-1" /> Preview</Button>
              </div>

              {fields.length === 0 ? (
                <div className="border border-dashed border-border rounded-md p-6 text-center text-xs text-muted-foreground">
                  Add fields using the buttons below
                </div>
              ) : (
                <div className="space-y-2">
                  {fields.map((field, i) => {
                    const Icon = getIcon(field.type);
                    const typeInfo = FIELD_TYPES.find(t => t.value === field.type);
                    const isDropTarget = dropIdx === i && dragIdx !== null && dragIdx !== i;
                    return (
                      <div
                        key={field.id}
                        className={`border rounded-md p-3 transition-all ${isDropTarget ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border bg-muted/20"}`}
                        data-testid={`form-field-${i}`}
                        draggable
                        onDragStart={e => handleFieldDragStart(e, i)}
                        onDragEnd={handleFieldDragEnd}
                        onDragOver={e => handleFieldDragOver(e, i)}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <DotsSixVertical size={16} className="text-muted-foreground cursor-grab active:cursor-grabbing shrink-0" weight="bold" />
                          <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            field.type === "text" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" :
                            field.type === "textarea" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" :
                            field.type === "number" ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" :
                            field.type === "checkbox" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" :
                            field.type === "dropdown" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" :
                            field.type === "date" ? "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" :
                            field.type === "photo" ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300" :
                            field.type === "section" ? "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100" :
                            "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                          }`}>
                            <Icon size={10} /> {typeInfo?.label}
                          </span>
                          {field.required && <span className="text-[9px] text-red-500 font-semibold">Required</span>}
                          <div className="flex-1" />
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-destructive" onClick={() => removeField(i)}><Trash size={10} /></Button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <div>
                            <Input value={field.label} onChange={e => updateField(i, "label", e.target.value)} placeholder={field.type === "section" ? "Section title *" : "Field label *"} className="text-xs h-7" data-testid={`field-label-${i}`} />
                          </div>
                          {["text", "textarea", "number"].includes(field.type) && (
                            <Input value={field.placeholder} onChange={e => updateField(i, "placeholder", e.target.value)} placeholder="Placeholder text" className="text-xs h-7" />
                          )}
                          {field.type === "section" && (
                            <Input value={field.placeholder} onChange={e => updateField(i, "placeholder", e.target.value)} placeholder="Short description shown under the heading (optional)" className="text-xs h-7" data-testid={`field-section-desc-${i}`} />
                          )}
                        </div>
                        {/* Inline preview of what the field looks like */}
                        {field.label && (
                          <div className="mt-2 p-2 bg-background/50 rounded border border-border/50">
                            <p className="text-[10px] text-muted-foreground mb-1 font-medium">Preview:</p>
                            {field.type === "text" && <div className="h-7 rounded border border-border bg-muted/30 px-2 flex items-center text-[10px] text-muted-foreground">{field.placeholder || field.label}</div>}
                            {field.type === "textarea" && <div className="h-12 rounded border border-border bg-muted/30 px-2 pt-1 text-[10px] text-muted-foreground">{field.placeholder || field.label}</div>}
                            {field.type === "number" && <div className="h-7 rounded border border-border bg-muted/30 px-2 flex items-center text-[10px] text-muted-foreground">123</div>}
                            {field.type === "checkbox" && <div className="flex items-center gap-1.5"><div className="h-3.5 w-3.5 rounded border border-border bg-muted/30" /><span className="text-[10px] text-muted-foreground">Yes / No</span></div>}
                            {field.type === "dropdown" && <div className="h-7 rounded border border-border bg-muted/30 px-2 flex items-center justify-between text-[10px] text-muted-foreground"><span>{(field.options || []).filter(Boolean)[0] || "Select..."}</span><CaretDown size={8} /></div>}
                            {field.type === "date" && <div className="h-7 rounded border border-border bg-muted/30 px-2 flex items-center text-[10px] text-muted-foreground gap-1"><Calendar size={10} /> dd/mm/yyyy</div>}
                            {field.type === "photo" && <div className="h-10 rounded border border-dashed border-border bg-muted/20 flex items-center justify-center text-[10px] text-muted-foreground gap-1"><Camera size={10} /> Tap to take photo</div>}
                            {field.type === "signature" && <div className="h-10 rounded border border-dashed border-border bg-muted/20 flex items-center justify-center text-[10px] text-muted-foreground gap-1"><PenNib size={10} /> Sign here</div>}
                            {field.type === "section" && <div className="border-l-2 border-primary pl-2"><p className="text-[11px] font-bold">{field.label}</p>{field.placeholder && <p className="text-[9px] text-muted-foreground">{field.placeholder}</p>}<p className="text-[9px] text-muted-foreground italic mt-0.5">Fields below sit under this heading on the client report</p></div>}
                          </div>
                        )}
                        {field.type === "dropdown" && (
                          <div className="mt-2">
                            <p className="text-[10px] text-muted-foreground mb-1">Options (one per line):</p>
                            <Textarea
                              value={(field.options || []).join("\n")}
                              onChange={e => updateField(i, "options", e.target.value.split("\n"))}
                              rows={3}
                              className="text-xs font-mono"
                              placeholder="Option 1&#10;Option 2&#10;Option 3"
                            />
                          </div>
                        )}
                        {field.type !== "section" && <div className="flex items-center gap-2 mt-2">
                          <Switch checked={field.required} onCheckedChange={v => updateField(i, "required", v)} id={`req-${field.id}`} />
                          <Label htmlFor={`req-${field.id}`} className="text-[10px]">Required</Label>
                        </div>}
                      </div>
                    );
                  })}
                  <p className="text-[9px] text-muted-foreground text-center mt-1">Drag fields to reorder</p>
                </div>
              )}

              {/* Add field buttons */}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {FIELD_TYPES.map(ft => (
                  <Button key={ft.value} variant="outline" size="sm" className="text-[10px] h-7" onClick={() => addField(ft.value)} data-testid={`add-field-${ft.value}`}>
                    <ft.icon size={12} className="mr-1" /> {ft.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditor(false); resetEditor(); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-form-btn">{saving ? "Saving..." : editingId ? "Update Form" : "Create Form"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="form-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Preview: {title || "Untitled"}</DialogTitle>
            <DialogDescription>This is how the form will appear to vendors.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {fields.map((field, i) => field.type === "section" ? (
              <div key={field.id} className="pt-3 border-t border-border" data-testid={`preview-section-${i}`}>
                <p className="text-sm font-bold">{field.label || `Section ${i + 1}`}</p>
                {field.placeholder && <p className="text-xs text-muted-foreground">{field.placeholder}</p>}
              </div>
            ) : (
              <div key={field.id}>
                <Label className="text-sm">{field.label || `Field ${i + 1}`} {field.required && <span className="text-red-500">*</span>}</Label>
                {field.type === "text" && <Input placeholder={field.placeholder} className="mt-1" disabled />}
                {field.type === "textarea" && <Textarea placeholder={field.placeholder} rows={3} className="mt-1" disabled />}
                {field.type === "number" && <Input type="number" placeholder={field.placeholder} className="mt-1" disabled />}
                {field.type === "date" && <DateInput className="mt-1" disabled />}
                {field.type === "checkbox" && <div className="flex items-center gap-2 mt-1"><input type="checkbox" disabled className="rounded" /><span className="text-xs text-muted-foreground">Yes / No</span></div>}
                {field.type === "dropdown" && (
                  <Select disabled><SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger></Select>
                )}
                {field.type === "photo" && <div className="mt-1 border border-dashed border-border rounded-md p-4 text-center text-xs text-muted-foreground">Photo upload area</div>}
                {field.type === "signature" && <div className="mt-1 border border-dashed border-border rounded-md p-6 text-center text-xs text-muted-foreground">Signature pad</div>}
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={() => setShowPreview(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
